import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { ClientDataBuilder } from './client-data.builder';
import { ClientDataService } from './client-data.service';
import { Client } from './client.model';
import { EntityDataEffects } from '../entity-data/entity-data.effects';
import { IState } from './client-data.reducer';
import { TranslateService } from '@ngx-translate/core';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { DialogService } from '../dialog/dialog.service';
import { NavigationService } from '../navigation/navigation.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { ClientDataStore } from './client-data.store';
import {
  ActionsEnum as SettingsActionsEnum,
  EmitCurrencyCodeChanged,
} from '../settings-data/settings-data.actions';
import {
  ALL_LIST_FILTER,
  IEntityData,
  ISearchParams,
  SearchParamsEnum,
} from '../entity-data/entity-data.data';
import { concat, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  flatMap,
  map,
  mapTo,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  ActionsEnum,
  CreateClientBankDetails,
  CreateClientBankDetailsError,
  createClientBankDetailsError,
  DeleteDocumentInClientDocuments,
  deleteDocumentInClientDocuments,
  FetchClientDocuments,
  fetchClientDocumentsError,
  FetchClientDocumentsError,
  FetchNextClientDocumentsPage,
  FilterClientDocuments,
  InvoiceClient,
  SaveEditingClient,
  SendBankDetailsToEmail,
  sendBankDetailsToEmailError,
  SendBankDetailsToEmailError,
  sendBankDetailsToEmailSuccess,
  SendBankDetailsToEmailSuccess,
  setClientDocuments,
  SetClientDocuments,
  showJobDialog,
  UpdateDocumentInClientDocuments,
  updateDocumentInClientDocuments,
} from './client-data.actions';
import { INVOICES_MASTER_ROUTE } from '../main/main.data';
import {
  ADD_CLIENT_QUERY_PARAM,
  DOCUMENTS_NEW_ROUTE,
  DocumentTypesEnum,
} from '../document-data/document-data.data';
import { DocumentHistoryFilterTypesEnum } from './client-data.data';
import { Settings } from '../settings-data/settings.model';
import { Document } from '../document-data/document.model';
import { List } from 'immutable';
import {
  ActionsEnum as EntityActionsEnum,
  clearList,
  ClearList,
  DeleteEntityNetworkSuccess,
  saveEditingEntity,
  SaveEntityNetworkSuccess,
  setDispatchedResponse,
  SetDispatchedResponse,
} from '../entity-data/entity-data.actions';
import { Reference } from '../document-data/sub-models/reference.model';
import { ERRORS } from '../shared/constants/errors.constant';
import * as HttpStatus from 'http-status-codes';
import { EntityTypesEnum } from '../../app.enum';
import { AppStore } from '../../store';
import {
  ActionsEnum as InvoiceActionsEnum,
  BatchMarkInvoiceAsPaidNetworkSuccess,
  CreateManualPaymentSuccess,
  TransactionsUpdateSuccess,
} from '../invoice-data/invoice-data.actions';
import { InvoiceDataStore } from '../invoice-data/invoice-data.store';
import { Schedule } from '../document-data/sub-models/schedule.model';
import { showToast, ShowToast } from '../toaster/toaster.actions';
import { IToasterConfig, IToasterData } from '../toaster/toaster.data';
import { MessageToastComponent } from '../shared/components/message-toast/message-toast.component';

type UpdateClientDocumentsTrigger =
  | SaveEntityNetworkSuccess
  | DeleteEntityNetworkSuccess
  | CreateManualPaymentSuccess
  | BatchMarkInvoiceAsPaidNetworkSuccess
  | TransactionsUpdateSuccess;

@Injectable()
export class ClientDataEffects extends EntityDataEffects<IState, Client> {
  private _documentsComparatorFn: (a: Document, b: Document) => number;

  constructor(
    private _clientDataStore: ClientDataStore,
    private _clientDataService: ClientDataService,
    private _invoiceDataStore: InvoiceDataStore,
    actions: Actions,
    appStore: AppStore,
    clientDataBuilder: ClientDataBuilder,
    translateService: TranslateService,
    globalBannerService: GlobalBannerService,
    dialogService: DialogService,
    navigationService: NavigationService,
    settingsDataStore: SettingsDataStore,
    featuresDataStore: FeaturesDataStore,
    localStorageService: LocalStorageService,
    featuresDataDispatcher: FeaturesDataDispatcher
  ) {
    super(
      actions,
      appStore,
      _clientDataService,
      _clientDataStore,
      clientDataBuilder,
      settingsDataStore,
      featuresDataStore,
      translateService,
      globalBannerService,
      dialogService,
      navigationService,
      localStorageService,
      featuresDataDispatcher
    );

    this._documentsComparatorFn = clientDataBuilder.buildDocumentsComparatorFn();
  }

  @Effect() loadSearchParams = this.loadSearchParams;
  @Effect() persistSearchParams = this.persistSearchParams;
  @Effect() fetchList = this.fetchList;
  @Effect() searchList = this.searchList;
  @Effect() fetchNextPage = this.fetchNextPage;
  @Effect() getEntityById = this.getEntityById;
  @Effect() saveNewEntity = this.saveNewEntity;
  @Effect() saveNewEntityError = this.saveNewEntityError;
  @Effect() updateEntity = this.updateEntity;
  @Effect() createNewEditingEntity = this.createNewEditingEntity;
  @Effect() refreshEditingEntity = this.refreshEditingEntity;
  @Effect() setEditingEntityContent = this.setEditingEntityContent;
  @Effect() setEditingEntityProperties = this.setEditingEntityProperties;
  @Effect() setEditingEntityField = this.setEditingEntityField;
  @Effect() saveEditingEntity = this.saveEditingEntity;
  @Effect() saveExistingEntityError = this.saveExistingEntityError;
  @Effect() saveEntityStore = this.saveEntityStore;
  @Effect() deleteEntityStore = this.deleteEntityStore;
  @Effect() saveEntityToNetwork = this.saveEntityToNetwork;
  @Effect() deleteEntityNetwork = this.deleteEntityNetwork;

  /**
   * Save editing client
   */
  @Effect()
  saveEditingClient: Observable<any> = this.actions.pipe(
    ofType(ActionsEnum.SAVE_EDITING_CLIENT),
    withLatestFrom(this._clientDataStore.getEditingEntity()),
    filter(([_, client]: [SaveEditingClient, Client]) => Boolean(client)),
    flatMap(([{ payload }, client]: [SaveEditingClient, Client]) => {
      const { dispatchId } = payload;
      const isUnique$ = !client.id
        ? this._clientDataService.isUniqueClient(client)
        : of(true);
      const isNewClient = !client.id;

      return isUnique$.pipe(
        flatMap((isUnique: boolean) => {
          const actions = [];

          if (isUnique) {
            actions.push(saveEditingEntity(dispatchId, this.entityType));

            if (isNewClient) {
              actions.push(showJobDialog(this.entityType, true));
            }
          } else {
            const error = new Error('duplicate client');
            const errorData = [
              HttpStatus.BAD_REQUEST,
              { code: ERRORS.DUPLICATE_ENTITY },
              error,
            ];

            actions.push(
              setDispatchedResponse(
                this.entityType,
                dispatchId,
                ActionsEnum.SAVE_EDITING_CLIENT,
                false,
                client,
                error,
                errorData
              )
            );
          }

          return concat(actions);
        })
      );
    })
  );

  /**
   * Update currency code param on search params
   */
  @Effect({ dispatch: false })
  updateBaseSearchParams = this.actions.pipe(
    ofType(SettingsActionsEnum.EMIT_CURRENCY_CODE_CHANGED),
    tap(({ payload }: EmitCurrencyCodeChanged) => {
      const { currency } = payload;

      this.baseSearchParams = {
        ...this.baseSearchParams,
        [SearchParamsEnum.currency_code]: currency,
      };
    })
  );

  /**
   * Invoice client
   */
  @Effect({ dispatch: false })
  invoiceClient = this.actions.pipe(
    ofType(ActionsEnum.INVOICE_CLIENT),
    tap(({ payload }: InvoiceClient) => {
      const { clientId } = payload;
      const queryParams = {
        [ADD_CLIENT_QUERY_PARAM]: clientId,
      };

      void this.navigationService.goTo(
        [INVOICES_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
        {
          queryParams,
        }
      );
    })
  );

  /**
   * Clear clients list on invoice updates
   */
  @Effect()
  clearListOnInvoiceUpdates: Observable<ClearList> = this.actions.pipe(
    ofType(
      EntityActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS,
      EntityActionsEnum.DELETE_ENTITY_NETWORK_SUCCESS,
      InvoiceActionsEnum.CREATE_MANUAL_PAYMENT_SUCCESS,
      InvoiceActionsEnum.MARK_AS_PAID_SUCCESS,
      InvoiceActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK_SUCCESS,
      InvoiceActionsEnum.TRANSACTIONS_UPDATE_SUCCESS
    ),
    filter(
      ({ payload }: UpdateClientDocumentsTrigger) =>
        payload.entityType === EntityTypesEnum.invoice
    ),
    mapTo(clearList(this.entityType, ALL_LIST_FILTER))
  );

  /*************************
   * CLIENT DOCUMENT HISTORY
   *************************/
  /**
   * Fetch client documents
   */
  @Effect()
  fetchClientDocuments: Observable<
    SetClientDocuments | FetchClientDocumentsError
  > = this.actions.pipe(
    ofType(ActionsEnum.FETCH_CLIENT_DOCUMENTS),
    switchMap(({ payload }: FetchClientDocuments) => {
      const { clientId, filter: filterFromPayload } = payload;

      return this.settingsDataStore.getSettings().pipe(
        flatMap((settings: Settings) =>
          this._clientDataService.getClientDocuments(
            clientId,
            filterFromPayload,
            {},
            settings
          )
        ),
        withLatestFrom(this._invoiceDataStore.schedules$),
        map(
          ([{ entities, searchParams }, schedules]: [
            IEntityData,
            List<Schedule>
          ]) => {
            const documents = this._attachSchedulesToInvoices(
              entities[0] as List<Document>,
              schedules
            );

            return setClientDocuments(
              this.entityType,
              documents,
              searchParams,
              this._documentsComparatorFn
            );
          }
        ),
        catchError((error) =>
          of(
            fetchClientDocumentsError(
              this.entityType,
              new Error('fetch client documents error'),
              error
            )
          )
        )
      );
    })
  );

  /**
   * Fetch next client documents page
   */
  @Effect()
  fetchNextClientDocumentsPage: Observable<
    SetClientDocuments | FetchClientDocumentsError
  > = this.actions.pipe(
    ofType(ActionsEnum.FETCH_NEXT_CLIENT_DOCUMENTS_PAGE),
    switchMap(({ payload }: FetchNextClientDocumentsPage) => {
      const { clientId } = payload;

      return this._clientDataStore.getClientDocumentsSearchParams().pipe(
        filter((searchParams: ISearchParams) => !!searchParams.next_page_url),
        withLatestFrom(
          this._clientDataStore.getClientDocumentsFilter(),
          this.settingsDataStore.settings$
        ),
        take(1),
        flatMap(
          ([searchParams, filterClientDocuments, settings]: [
            ISearchParams,
            DocumentHistoryFilterTypesEnum,
            Settings
          ]) => {
            const params: ISearchParams = this.entityDataBuilder.buildSearchParamsFromUrl(
              searchParams.next_page_url
            );

            return this._clientDataService.getClientDocuments(
              clientId,
              filterClientDocuments,
              params,
              settings
            );
          }
        ),
        withLatestFrom(this._invoiceDataStore.schedules$),
        map(
          ([{ entities, searchParams }, schedules]: [
            IEntityData,
            List<Schedule>
          ]) => {
            const documents = this._attachSchedulesToInvoices(
              entities[0] as List<Document>,
              schedules
            );

            return setClientDocuments(
              this.entityType,
              documents,
              searchParams,
              this._documentsComparatorFn
            );
          }
        ),
        catchError((error) =>
          of(
            fetchClientDocumentsError(
              this.entityType,
              new Error('fetch next client document page error'),
              error
            )
          )
        )
      );
    })
  );

  /**
   * Filter client documents page
   */
  @Effect()
  filterClientDocuments: Observable<
    SetClientDocuments | FetchClientDocumentsError
  > = this.actions.pipe(
    ofType(ActionsEnum.FILTER_CLIENT_DOCUMENTS),
    switchMap(({ payload }: FilterClientDocuments) => {
      const { clientId, filter: filterFromPayload } = payload;

      return this.settingsDataStore.getSettings().pipe(
        flatMap((settings: Settings) =>
          this._clientDataService.getClientDocuments(
            clientId,
            filterFromPayload,
            {},
            settings
          )
        ),
        withLatestFrom(this._invoiceDataStore.schedules$),
        map(
          ([{ entities, searchParams }, schedules]: [
            IEntityData,
            List<Schedule>
          ]) => {
            const documents = this._attachSchedulesToInvoices(
              entities[0] as List<Document>,
              schedules
            );

            return setClientDocuments(
              this.entityType,
              documents,
              searchParams,
              this._documentsComparatorFn
            );
          }
        ),
        catchError((error) =>
          of(
            fetchClientDocumentsError(
              this.entityType,
              new Error('filter client documents error'),
              error
            )
          )
        )
      );
    })
  );

  /**
   * Update client documents on document changes
   */
  @Effect()
  updateClientDocumentsOnDocumentUpdates: Observable<
    UpdateDocumentInClientDocuments | DeleteDocumentInClientDocuments | never
  > = this.actions.pipe(
    ofType(
      EntityActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS,
      EntityActionsEnum.DELETE_ENTITY_NETWORK_SUCCESS,
      InvoiceActionsEnum.CREATE_MANUAL_PAYMENT_SUCCESS,
      InvoiceActionsEnum.MARK_AS_PAID_SUCCESS
    ),
    withLatestFrom(this._clientDataStore.getClientDocuments()),
    filter(
      ([{ payload }, clientDocuments]: [
        UpdateClientDocumentsTrigger,
        List<Document>
      ]) => {
        const document: Document = payload['entity'] || payload['invoice'];
        const onDocumentActions = [
          EntityTypesEnum.invoice,
          EntityTypesEnum.estimate,
          EntityTypesEnum.purchase_order,
          EntityTypesEnum.credit_memo,
        ].includes(payload.entityType);

        return (
          onDocumentActions &&
          document &&
          clientDocuments &&
          clientDocuments.some((clientDocument: Document) => {
            const clientDocumentReference: Reference =
              clientDocument.billing.reference;
            const documentReference: Reference = document.billing.reference;
            const hasSameBillingName =
              clientDocument.billingName === document.billingName;
            const hasSameReferenceId =
              clientDocumentReference &&
              documentReference &&
              clientDocumentReference.id === documentReference.id;

            return hasSameReferenceId || hasSameBillingName;
          })
        );
      }
    ),
    mergeMap(
      ([{ payload }, _]: [UpdateClientDocumentsTrigger, List<Document>]) => {
        const document: Document = payload['entity'] || payload['invoice'];
        const action = payload.sourceAction;

        switch (action) {
          case EntityActionsEnum.DELETE_ENTITY_NETWORK:
            return of(
              deleteDocumentInClientDocuments(this.entityType, document)
            );
          case EntityActionsEnum.SAVE_ENTITY_NETWORK:
          case EntityActionsEnum.UPDATE_ENTITY:
          case InvoiceActionsEnum.CREATE_MANUAL_PAYMENT:
          case InvoiceActionsEnum.MARK_AS_PAID:
            return of(
              updateDocumentInClientDocuments(this.entityType, document)
            );
          default:
            return EMPTY;
        }
      }
    )
  );

  @Effect()
  createClientBankDetails: Observable<
    SetDispatchedResponse | CreateClientBankDetailsError
  > = this.actions.pipe(
    ofType(ActionsEnum.CREATE_CLIENT_BANK_DETAILS),
    exhaustMap(({ payload }: CreateClientBankDetails) => {
      // using exhaustMap to ensure only once create bank transfer is called
      return this._clientDataService
        .createClientBankDetails(payload.clientId)
        .pipe(
          map((client) =>
            setDispatchedResponse(
              this.entityType,
              payload.dispatchId,
              'CREATE_CLIENT_BANK_DETAILS',
              true,
              client
            )
          ),
          catchError((error) =>
            of(
              createClientBankDetailsError(
                this.entityType,
                new Error('fetch client bank details error'),
                error
              )
            )
          )
        );
    })
  );

  @Effect({ dispatch: false })
  createClientBankDetailsError: Observable<any> = this.actions.pipe(
    ofType(ActionsEnum.CREATE_CLIENT_BANK_DETAILS_ERROR),
    tap(({ payload }: CreateClientBankDetailsError) =>
      this.globalBannerService.showSystemErrorBanner(payload.error)
    )
  );

  @Effect()
  sendBankDetailsToEmail: Observable<
    SendBankDetailsToEmailSuccess | SendBankDetailsToEmailError
  > = this.actions.pipe(
    ofType(ActionsEnum.SEND_BANK_DETAILS_TO_EMAIL),
    switchMap(({ payload }: SendBankDetailsToEmail) => {
      const { clientId, email } = payload;

      return this._clientDataService
        .sendBankDetailsToEmail(clientId, email)
        .pipe(
          map(() => sendBankDetailsToEmailSuccess(clientId, email)),
          catchError((error) =>
            of(
              sendBankDetailsToEmailError(
                new Error('send bank details via email error'),
                error
              )
            )
          )
        );
    })
  );

  @Effect()
  sendBankDetailsToEmailSuccess: Observable<ShowToast> = this.actions.pipe(
    ofType(ActionsEnum.SEND_BANK_DETAILS_TO_EMAIL_SUCCESS),
    map(({ payload }: SendBankDetailsToEmailSuccess) => {
      const { email } = payload;
      const toastData: IToasterData = {
        label: this.translateService.instant(
          'CLIENT.BANK_DETAILS.SHARE_EMAIL_SUCCESS',
          { email }
        ),
        containsHtml: true,
      };
      const toastConfig: IToasterConfig = {
        type: 'message-copied-clipboard',
        component: MessageToastComponent,
        data: toastData,
      };

      return showToast(toastConfig);
    })
  );

  private _attachSchedulesToInvoices(
    documents: List<Document>,
    schedules: List<Schedule>
  ) {
    return documents
      ? (documents.map((doc: Document) => {
          return doc.documentType === DocumentTypesEnum.invoice
            ? doc.setIn(
                [Document.KEYS.SCHEDULE],
                new Schedule(
                  schedules.find(
                    (schedule: Schedule) => schedule.invoiceId === doc.id
                  )
                )
              )
            : doc;
        }) as List<Document>)
      : null;
  }
}
